import React, { useState, useRef, useEffect } from 'react';

import './AIChat.css';

import aiChatService from '../../services/AIChatService';

// Function to parse and highlight text wrapped in ** markers
const parseHighlightedText = (text) => {
  if (!text) return text;
  
  // Split by ** markers and process each part
  const parts = text.split(/(\*\*.*?\*\*)/g);
  
  return parts.map((part, index) => {
    // Check if this part is wrapped in ** markers
    if (part.startsWith('**') && part.endsWith('**')) {
      // Remove the ** markers and wrap in highlight span
      const highlightedText = part.slice(2, -2);
      return (
        <span key={index} className="ai-highlighted-text">
          {highlightedText}
        </span>
      );
    }
    // Return regular text
    return part;
  });
};

const AIChat = ({ isOpen, onClose }) => {
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'ai',
      content: "Hi there! I'm your Talenton assistant. I'm here to help you get the most out of Talenton - what can I assist with?",
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showChatHistoryButton, setShowChatHistoryButton] = useState(false);
  const [currentView, setCurrentView] = useState('welcome'); // 'welcome', 'history', 'chat'
  const [navigationStack, setNavigationStack] = useState(['welcome']);
  const [chatHistory, setChatHistory] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [currentChatId, setCurrentChatId] = useState(null);
  const [isFirstMessage, setIsFirstMessage] = useState(true);
  const [loadingChatId, setLoadingChatId] = useState(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const chatModalRef = useRef(null);
  const deleteModalRef = useRef(null);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [chatToDelete, setChatToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [copiedMessageId, setCopiedMessageId] = useState(null);

  // Load chat history on component mount
  useEffect(() => {
    if (isOpen) {
      loadChatHistory();
    }
  }, [isOpen]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Load chat history from database
  const loadChatHistory = async () => {
    setIsLoadingHistory(true);
    try {
      console.log('Loading chat history...');
      const response = await aiChatService.getChatHistory(1, 50);
      console.log('Chat history response:', response);
      if (response.success) {
        setChatHistory(response.data.data.chats || []);
        console.log('Chat history loaded:', response.data.data.chats);
      } else {
        console.error('Failed to load chat history:', response.error);
      }
    } catch (error) {
      console.error('Error loading chat history:', error);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Observe scroll position to toggle the scroll-to-bottom button
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);
      // Only show the button when the user has scrolled up more than 120px
      setShowScrollToBottom(distanceFromBottom > 120);
    };

    handleScroll();
    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [currentView]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showChatHistoryButton && !event.target.closest('.ai-chat-menu-container')) {
        setShowChatHistoryButton(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showChatHistoryButton]);

  // Close chat window when clicking outside
  useEffect(() => {
    const handleClickOutsideChat = (event) => {
      // Don't close if clicking inside the delete modal
      if (deleteModalRef.current && deleteModalRef.current.contains(event.target)) {
        return;
      }
      
      if (isOpen && chatModalRef.current && !chatModalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutsideChat);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutsideChat);
    };
  }, [isOpen, onClose]);

  const handleSendMessage = async () => {
    const messageToSend = inputValue;
    if (!messageToSend.trim()) return;

    // If we're on the welcome page, navigate to chat view
    if (currentView === 'welcome') {
      navigateToView('chat');
    }
    
    // If we're in history view, check if we should continue existing chat or start new one
    if (currentView === 'history') {
      if (currentChatId) {
        // Continue existing chat - navigate to chat view with current chat
        navigateToView('chat', currentChatId);
      } else {
        // No chat selected - start new chat
        setCurrentChatId(null); // Clear any existing chat ID
        setIsFirstMessage(true); // Mark as first message for new chat
        navigateToView('chat');
      }
    }

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: messageToSend,
      timestamp: new Date()
    };
    
    setMessages(prev => [...prev, userMessage]);
    const currentInput = messageToSend;
    setInputValue('');
    setIsTyping(true);
   
    try {
      // Call the actual API
      const response = await aiChatService.sendUserMessageV2(currentInput);
      
      if (response.success) {
        // Debug: Log the full response structure
        console.log('AI API Response:', response.data);
        
        // Parse the API response structure
        let aiContent = '';
        let suggestions = [];
        
        if (response.data && response.data.data) {
          // Check if aiResult indicates no records found and use queryResult.message instead
          if (response.data.data.aiResult && 
              response.data.data.aiResult.toLowerCase().includes('no records were found') && 
              response.data.data.queryResult && 
              response.data.data.queryResult.message) {
            aiContent = response.data.data.queryResult.message;
          } else {
            // Use the aiResult if available, otherwise fall back to response
            aiContent = response.data.data.aiResult || response.data.data.response || 'I received your query but couldn\'t generate a proper response.';
          }
          
          // If there's query data, we could also show some summary information
          if (response.data.data.queryResult) {
            const queryResult = response.data.data.queryResult;
          }
          
          // Extract suggestions separately
          if (response.data.data.suggestions && response.data.data.suggestions.length > 0) {
            suggestions = response.data.data.suggestions.slice(0, 3);
          }
        } else {
          // Fallback for different response structures
          aiContent = response.data.answer || response.data.response || JSON.stringify(response.data);
        }
        
        const aiResponse = {
          id: Date.now() + 1,
          type: 'ai',
          content: aiContent,
          suggestions: suggestions,
          // Include processing time meta (for "Thought for Xs") if available
          processingMs: response.data?.meta?.processingMs || null,
          timestamp: new Date()
        };
        // Hide typing indicator before rendering the AI message to avoid overlap
        setIsTyping(false);
        setMessages(prev => [...prev, aiResponse]);

        // Save messages to database only if we have valid AI response
        if (aiContent && aiContent.trim().length > 0) {
          try {
            const firstQuestion = isFirstMessage ? currentInput : null;
            
            const userSaveResult = await aiChatService.saveMessageToHistory(userMessage, firstQuestion);
            const aiSaveResult = await aiChatService.saveMessageToHistory(aiResponse, firstQuestion);
            
          if (isFirstMessage) {
            setIsFirstMessage(false);
          }
          
          // Always reload chat history from database to ensure consistency
          console.log('Reloading chat history from database...');
          loadChatHistory();
          } catch (e) {
            console.error('Failed to save messages to database', e);
          }
        } else {
          console.log('Skipping database save - no valid AI response content');
        }
      } else {
        // Fallback to dummy response if API fails
        const aiResponse = {
          id: Date.now() + 1,
          type: 'ai',
          content: generateDummyResponse(currentInput),
          timestamp: new Date()
        };
        setIsTyping(false);
        setMessages(prev => [...prev, aiResponse]);
        console.error('AI API Error:', response.error);
        
        // Save messages to database only if we have valid fallback response
        if (aiResponse.content && aiResponse.content.trim().length > 0) {
          try {
            const firstQuestion = isFirstMessage ? currentInput : null;
            
            const userSaveResult = await aiChatService.saveMessageToHistory(userMessage, firstQuestion);
            const aiSaveResult = await aiChatService.saveMessageToHistory(aiResponse, firstQuestion);
            
            if (isFirstMessage) {
              setIsFirstMessage(false);
              // Add new chat to history immediately for better UX
              console.log('Adding new chat to history immediately (fallback)...');
              const newChat = {
                _id: userSaveResult.data?.data?.chatId || Date.now().toString(),
                title: currentInput,
                date: new Date(),
                totalMessages: 2,
                lastUpdated: new Date()
              };
              setChatHistory(prev => [newChat, ...prev]);
            } else {
              // For existing chats, just reload to get updated message count
              console.log('Reloading chat history for existing chat (fallback)...');
              loadChatHistory();
            }
          } catch (e) {
            console.error('Failed to save fallback messages to database', e);
          }
        } else {
          console.log('Skipping database save - no valid fallback response content');
        }
      }
    } catch (error) {
      console.error('Error calling AI API:', error);
      // Fallback to dummy response on error
      const aiResponse = {
        id: Date.now() + 1,
        type: 'ai',
        content: generateDummyResponse(currentInput),
        timestamp: new Date()
      };
      setIsTyping(false);
      setMessages(prev => [...prev, aiResponse]);
      
      // Save messages to database only if we have valid error response
      if (aiResponse.content && aiResponse.content.trim().length > 0) {
        try {
          const firstQuestion = isFirstMessage ? currentInput : null;
          
          const userSaveResult = await aiChatService.saveMessageToHistory(userMessage, firstQuestion);
          const aiSaveResult = await aiChatService.saveMessageToHistory(aiResponse, firstQuestion);
          
          if (isFirstMessage) {
            setIsFirstMessage(false);
            // Add new chat to history immediately for better UX
            console.log('Adding new chat to history immediately (error)...');
            const newChat = {
              _id: userSaveResult.data?.data?.chatId || Date.now().toString(),
              title: currentInput,
              date: new Date(),
              totalMessages: 2,
              lastUpdated: new Date()
            };
            setChatHistory(prev => [newChat, ...prev]);
          } else {
            // For existing chats, just reload to get updated message count
            console.log('Reloading chat history for existing chat (error)...');
            loadChatHistory();
          }
        } catch (e) {
          console.error('Failed to save error fallback messages to database', e);
        }
      } else {
        console.log('Skipping database save - no valid error response content');
      }
    } finally {
      setIsTyping(false);
    }
  };

  const generateDummyResponse = (userInput) => {
    console.log('userInput', userInput);
    
    const responses = [
      "I understand you're asking about that. Let me help you with some information based on your question.",
      "That's a great question! Here's what I can tell you about that topic.",
      "I'd be happy to assist you with that. Let me provide you with some helpful information.",
      "Based on your question, here are some steps you can follow to resolve this issue.",
      "I can help you with that! Let me give you some guidance on how to proceed.",
      "That's an interesting question. Here's what I know about that subject.",
      "I'm here to help! Let me provide you with some useful information about your query.",
      "Great question! Here's what I can tell you to help you out."
    ];
    return responses[Math.floor(Math.random() * responses.length)];
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      handleSendMessage();
    }
  };

  const navigateToView = (view, chatId = null) => {
    setNavigationStack(prev => [...prev, view]);
    setCurrentView(view);
    
    if (view === 'history') {
      // Always load fresh data from database when viewing history
      loadChatHistory();
    } else if (view === 'chat' && chatId) {
      // Chat view with specific chat ID - messages already loaded in handleChatSelect
    } else if (view === 'chat' && !chatId) {
      // This is a new chat from welcome page
      // Keep the welcome message and add new messages to it
    } else if (view === 'welcome') {
      setMessages([
        {
          id: 1,
          type: 'ai',
          content: "Hi there! I'm your Talenton assistant. I'm here to help you get the most out of Talenton - what can I assist with?",
          timestamp: new Date()
        }
      ]);
    }
    
    setShowChatHistoryButton(false);
  };

  const handleBackClick = () => {
    if (navigationStack.length > 1) {
      const newStack = [...navigationStack];
      newStack.pop(); // Remove current view
      const previousView = newStack[newStack.length - 1];
      
      setNavigationStack(newStack);
      setCurrentView(previousView);
      
      if (previousView === 'history') {
        // History view - no additional action needed
      } else if (previousView === 'welcome') {
        // Reset to welcome message only
        setMessages([
          {
            id: 1,
            type: 'ai',
            content: "Hi there! I'm your Talenton assistant. I'm here to help you get the most out of Talenton - what can I assist with?",
            timestamp: new Date()
          }
        ]);
      }
    }
  };

  // Format chat date for display
  const formatChatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) {
      return 'Today';
    } else if (diffDays === 2) {
      return 'Yesterday';
    } else if (diffDays <= 7) {
      return date.toLocaleDateString('en-US', { weekday: 'long' });
    } else {
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      });
    }
  };

  const handleChatSelect = async (chatId) => {
    try {
      // Set loading state for the selected chat
      setLoadingChatId(chatId);
      
      const response = await aiChatService.getChatById(chatId);
      if (response.success) {
        const chat = response.data.data;
        setCurrentChatId(chatId);
        setMessages(chat.messages || []);
        navigateToView('chat', chatId);
      }
    } catch (error) {
      console.error('Error loading chat:', error);
    } finally {
      // Clear loading state
      setLoadingChatId(null);
    }
  };

  const handleChatHistoryClick = () => {
    navigateToView('history');
    // Always fetch fresh data from database when opening history
    loadChatHistory();
  };

  const handleDeleteChat = (chatId, e) => {
    e.stopPropagation();
    setChatToDelete(chatId);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteChat = async () => {
    if (chatToDelete) {
      try {
        setIsDeleting(true);
        const response = await aiChatService.deleteChat(chatToDelete);
        if (response.success) {
          // Remove from local state
          setChatHistory(prev => prev.filter(chat => chat._id !== chatToDelete));
          // If we're viewing the deleted chat, go back to history
          if (currentChatId === chatToDelete) {
            navigateToView('history');
            setCurrentChatId(null);
            setMessages([{
              id: 1,
              type: 'ai',
              content: "Hi there! I'm your Talenton assistant. I'm here to help you get the most out of Talenton - what can I assist with?",
              timestamp: new Date()
            }]);
          }
        }
      } catch (error) {
        console.error('Error deleting chat:', error);
      } finally {
        setIsDeleting(false);
        setShowDeleteConfirm(false);
        setChatToDelete(null);
      }
    }
  };

  const cancelDeleteChat = () => {
    setShowDeleteConfirm(false);
    setChatToDelete(null);
  };


  if (!isOpen) return null;

  return (
    <div className="ai-chat-overlay">
      <div ref={chatModalRef} className={`ai-chat-modal ${currentView === 'welcome' ? 'home-page' : ''}`}>
        {/* Header */}
        <div className="ai-chat-header">
          <div className="ai-chat-logo">
            <div className="ai-logo-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="118" height="42" viewBox="0 0 118 42" fill="none">
                <path d="M20.8907 0C32.4282 0 41.7813 9.35306 41.7813 20.8907C41.7813 32.4282 32.4282 41.7813 20.8907 41.7813C9.35306 41.7813 0 32.4282 0 20.8907C0 9.35306 9.35306 0 20.8907 0ZM20.8907 1.67125C10.2761 1.67125 1.67125 10.2761 1.67125 20.8907C1.67125 31.5052 10.2761 40.1101 20.8907 40.1101C31.5052 40.1101 40.1101 31.5052 40.1101 20.8907C40.1101 10.2761 31.5052 1.67125 20.8907 1.67125ZM23.6706 17.0999V23.6075H23.6695V23.6336L23.6467 23.6195L18.4436 20.425V20.4174H18.4425V20.4239L18.4436 20.425V26.7128L23.6467 23.6195L23.6706 23.6347V23.6075L29.038 20.4174V26.7422L18.4436 33.2477V26.7226L12.1166 30.6059V24.1755L18.436 20.4228L12.1166 16.5417V10.2364L23.6706 17.0999ZM27.7563 9.20168C27.8191 10.3706 28.4122 12.6164 31.3142 13.5136L31.2805 14.0544C30.6584 14.1648 29.7692 14.5656 29.038 15.2861C28.3584 15.9558 27.8208 16.8957 27.7563 18.1324L27.7498 18.3838H27.1927C27.1927 17.5648 26.7597 16.5414 26.0426 15.6821C25.3272 14.8248 24.3679 14.1777 23.3638 14.0566L23.3975 13.5017C23.9339 13.5017 24.8882 13.1404 25.7151 12.3712C26.53 11.6131 27.1927 10.484 27.1927 8.98298H27.7498L27.7563 9.20168Z" fill="url(#paint0_linear_2781_2880)"/>
                <path d="M116.087 33.4251C114.363 33.3304 113.985 32.5348 113.947 31.0575V26.3791H112.678V24.485H113.947V22.7992L116.239 21.5113V24.485H118V26.3791H116.22V30.8302C116.22 31.2658 116.428 31.4552 116.864 31.4742H117.905V33.4251H116.087Z" fill="url(#paint1_linear_2781_2880)"/>
                <path d="M103.832 33.4251V24.485H106.18V25.9434C106.654 24.9017 107.525 24.2956 108.794 24.2956C110.745 24.2956 111.9 25.6025 111.9 28.0648V33.4251H109.552V28.1974C109.552 26.9473 109.078 26.3033 107.98 26.3033C106.862 26.3033 106.18 27.2125 106.18 28.8793V33.4251H103.832Z" fill="url(#paint2_linear_2781_2880)"/>
                <path d="M97.0331 33.6523C95.12 33.6523 93.9268 32.6106 93.9268 31.0574C93.9268 29.0497 95.4231 28.5951 97.4308 28.1595C99.0787 27.8375 99.9121 27.7617 99.9689 27.2693V27.1746C99.9689 26.6442 99.4954 26.1518 98.2832 26.1518C97.0709 26.1518 96.5027 26.7579 96.4459 27.5534V27.686H94.1162C94.1919 25.6593 95.783 24.3145 98.3021 24.3145C100.727 24.3145 102.204 25.3752 102.204 27.7996V33.4251H99.95V32.2128C99.3817 33.1409 98.3589 33.6523 97.0331 33.6523ZM97.5823 31.9666C98.6999 31.9666 99.9121 31.3984 99.9689 29.7316V28.974C99.4575 29.277 98.6241 29.4285 97.8854 29.6179C96.9762 29.8452 96.2944 30.1483 96.2944 30.9438C96.2944 31.5499 96.7679 31.9666 97.5823 31.9666Z" fill="url(#paint3_linear_2781_2880)"/>
                <path d="M91.153 33.4251C89.4294 33.3304 89.0506 32.5348 89.0127 31.0575V26.3791H87.7437V24.485H89.0127V22.7992L91.3045 21.5113V24.485H93.066V26.3791H91.2856V30.8302C91.2856 31.2658 91.4939 31.4552 91.9296 31.4742H92.9713V33.4251H91.153Z" fill="url(#paint4_linear_2781_2880)"/>
                <path d="M83.568 33.6902C81.7307 33.6902 80.348 32.9136 79.5146 31.7961L81.3709 30.7354C81.8633 31.4363 82.6967 31.853 83.6248 31.853C84.4582 31.853 84.9696 31.4931 84.9696 30.9059C84.9696 30.2809 83.9847 30.1672 82.6778 29.8831C81.1246 29.5611 79.5715 28.955 79.5715 27.1746C79.5715 25.5456 81.1246 24.2766 83.1324 24.2766C84.9128 24.2766 86.2765 25.0721 87.1478 26.2465L85.2916 27.2314C84.7991 26.4927 84.0415 26.1139 83.1513 26.1139C82.3179 26.1139 81.7876 26.4738 81.7876 27.0041C81.7876 27.6102 82.299 27.7049 83.8521 28.008C85.4621 28.311 87.2236 28.9171 87.2236 30.7354C87.2236 32.4212 85.6704 33.6902 83.568 33.6902Z" fill="url(#paint5_linear_2781_2880)"/>
                <path d="M75.9751 23.178V20.8862H78.4185V23.178H75.9751ZM76.013 33.4251V24.485H78.3806V33.4251H76.013Z" fill="url(#paint6_linear_2781_2880)"/>
                <path d="M71.1754 33.6902C69.3382 33.6902 67.9555 32.9136 67.1221 31.7961L68.9783 30.7354C69.4707 31.4363 70.3041 31.853 71.2322 31.853C72.0656 31.853 72.577 31.4931 72.577 30.9059C72.577 30.2809 71.5921 30.1672 70.2852 29.8831C68.732 29.5611 67.1789 28.955 67.1789 27.1746C67.1789 25.5456 68.732 24.2766 70.7398 24.2766C72.5202 24.2766 73.884 25.0721 74.7552 26.2465L72.899 27.2314C72.4066 26.4927 71.6489 26.1139 70.7587 26.1139C69.9253 26.1139 69.395 26.4738 69.395 27.0041C69.395 27.6102 69.9064 27.7049 71.4595 28.008C73.0695 28.311 74.831 28.9171 74.831 30.7354C74.831 32.4212 73.2779 33.6902 71.1754 33.6902Z" fill="url(#paint7_linear_2781_2880)"/>
                <path d="M62.944 33.6902C61.1067 33.6902 59.724 32.9136 58.8906 31.7961L60.7468 30.7354C61.2393 31.4363 62.0727 31.853 63.0008 31.853C63.8342 31.853 64.3456 31.4931 64.3456 30.9059C64.3456 30.2809 63.3607 30.1672 62.0537 29.8831C60.5006 29.5611 58.9474 28.955 58.9474 27.1746C58.9474 25.5456 60.5006 24.2766 62.5083 24.2766C64.2888 24.2766 65.6525 25.0721 66.5238 26.2465L64.6676 27.2314C64.1751 26.4927 63.4175 26.1139 62.5273 26.1139C61.6939 26.1139 61.1635 26.4738 61.1635 27.0041C61.1635 27.6102 61.6749 27.7049 63.2281 28.008C64.8381 28.311 66.5996 28.9171 66.5996 30.7354C66.5996 32.4212 65.0464 33.6902 62.944 33.6902Z" fill="url(#paint8_linear_2781_2880)"/>
                <path d="M52.6034 33.6523C50.6903 33.6523 49.4971 32.6106 49.4971 31.0574C49.4971 29.0497 50.9934 28.5951 53.0011 28.1595C54.649 27.8375 55.4824 27.7617 55.5392 27.2693V27.1746C55.5392 26.6442 55.0657 26.1518 53.8535 26.1518C52.6413 26.1518 52.073 26.7579 52.0162 27.5534V27.686H49.6865C49.7622 25.6593 51.3533 24.3145 53.8724 24.3145C56.2968 24.3145 57.7742 25.3752 57.7742 27.7996V33.4251H55.5203V32.2128C54.952 33.1409 53.9292 33.6523 52.6034 33.6523ZM53.1527 31.9666C54.2702 31.9666 55.4824 31.3984 55.5392 29.7316V28.974C55.0278 29.277 54.1944 29.4285 53.4557 29.6179C52.5465 29.8452 51.8647 30.1483 51.8647 30.9438C51.8647 31.5499 52.3382 31.9666 53.1527 31.9666Z" fill="url(#paint9_linear_2781_2880)"/>
                <path d="M60.9693 8.6533C62.285 8.6533 63.295 8.95944 63.9799 9.55421C64.6105 10.1128 64.8449 10.8156 64.8449 12.0948V16.0967C64.8449 17.0337 64.8808 17.1602 65.1692 17.5928H62.5557C62.4475 17.2864 62.4478 17.2317 62.3576 16.9073C61.5105 17.6102 60.8977 17.8267 59.8703 17.8267C57.942 17.8267 56.6629 16.7454 56.6628 15.1055C56.6628 13.4653 57.6896 12.6175 60.1228 12.221L61.51 11.9871C62.0868 11.897 62.2858 11.7524 62.2858 11.3919C62.2855 10.9056 61.7806 10.5999 60.9519 10.5998C60.0147 10.5998 59.5095 10.924 59.4014 11.627H57.1132C57.2754 9.6265 58.5184 8.65343 60.9693 8.6533ZM75.9475 8.6533C77.7856 8.65344 79.1732 9.57241 79.822 11.2124C80.1283 11.9693 80.2551 12.7806 80.2551 13.988H74.0727C74.235 15.1772 74.9376 15.8256 76.0367 15.8258C76.7216 15.8258 77.2088 15.5918 77.5512 15.0511H80.1463C79.6596 16.7452 78.0375 17.8267 75.9649 17.8267C73.2796 17.8266 71.4953 15.9885 71.4951 13.2492C71.4951 10.5097 73.316 8.6533 75.9475 8.6533ZM102.315 8.6533C105.019 8.65339 106.947 10.5272 106.947 13.3026C106.947 15.9339 105.001 17.8266 102.315 17.8267C99.6299 17.8267 97.7009 15.9342 97.7009 13.2307C97.7009 10.5272 99.6119 8.6533 102.315 8.6533ZM53.923 8.99495H55.4909V10.7979H53.923V14.1861C53.923 15.5558 53.9768 15.6636 54.5715 15.6636H55.4909V17.5928H53.923C52.8057 17.5928 52.2462 17.4305 51.8677 17.0161C51.4712 16.5835 51.3628 16.1145 51.3628 14.7627V10.7979H50.1377V8.99495H51.3628V6.23782H53.923V8.99495ZM69.6748 17.5928H67.1157V5.57084H69.6748V17.5928ZM86.9597 8.6348C88.852 8.63488 90.0051 9.8966 90.0051 11.9512V17.5928H87.446V12.8521C87.446 11.9695 87.3922 11.627 87.1762 11.3027C86.9599 10.9783 86.5811 10.7979 86.1306 10.7979C85.68 10.7979 85.3009 10.9594 85.0305 11.2657C84.7422 11.5901 84.653 11.9515 84.653 12.7085V17.5928H82.0928V8.88723H84.5801V9.73374C85.5172 8.86867 86.0585 8.6348 86.9597 8.6348ZM95.1059 8.99495H96.6738V10.7979H95.1059V14.1861C95.1059 15.5554 95.1601 15.6636 95.7544 15.6636H96.6738V17.5928H95.1059C93.9889 17.5927 93.4301 17.4303 93.0517 17.0161C92.6552 16.5835 92.5468 16.1145 92.5468 14.7627V10.7979H91.3217V8.99495H92.5468V6.23782H95.1059V8.99495ZM113.633 8.6348C115.526 8.6348 116.68 9.89654 116.68 11.9512V17.5928H114.121V12.8521C114.121 11.9693 114.066 11.6271 113.85 11.3027C113.634 10.9783 113.255 10.7979 112.804 10.7979C112.354 10.798 111.976 10.9596 111.705 11.2657C111.417 11.5901 111.327 11.9515 111.327 12.7085V17.5928H108.768V8.88723H111.255V9.73374C112.192 8.869 112.733 8.63489 113.633 8.6348ZM62.3576 13.4473C62.0874 13.5554 61.8531 13.627 61.4208 13.7171L60.5014 13.9151C59.5824 14.1314 59.1861 14.4382 59.186 14.9608C59.186 15.5374 59.672 15.8801 60.4829 15.8802C61.2938 15.8802 61.9431 15.52 62.3576 14.7812V13.4473ZM102.315 10.689C100.982 10.689 100.279 11.6086 100.279 13.2307C100.279 14.8707 101.017 15.7897 102.333 15.7899C103.648 15.7899 104.37 14.871 104.37 13.2851C104.37 11.6091 103.649 10.6891 102.315 10.689ZM75.929 10.5998C74.9378 10.5998 74.2884 11.1579 74.0901 12.1851H77.6231C77.5148 11.158 76.9201 10.5998 75.929 10.5998Z" fill="url(#paint10_linear_2781_2880)"/>
                <defs>
                    <linearGradient id="paint0_linear_2781_2880" x1="11.6059" y1="8.70444" x2="39.4601" y2="41.7813" gradientUnits="userSpaceOnUse">
                    <stop stop-color="#975EFF"/>
                    <stop offset="1" stop-color="#5DC8FA"/>
                    </linearGradient>
                    <linearGradient id="paint1_linear_2781_2880" x1="66.2932" y1="11.1417" x2="68.4751" y2="42.9769" gradientUnits="userSpaceOnUse">
                    <stop stop-color="#975EFF"/>
                    <stop offset="1" stop-color="#5DC8FA"/>
                    </linearGradient>
                    <linearGradient id="paint2_linear_2781_2880" x1="66.2928" y1="11.1417" x2="68.4747" y2="42.9769" gradientUnits="userSpaceOnUse">
                    <stop stop-color="#975EFF"/>
                    <stop offset="1" stop-color="#5DC8FA"/>
                    </linearGradient>
                    <linearGradient id="paint3_linear_2781_2880" x1="66.2932" y1="11.1417" x2="68.4751" y2="42.9769" gradientUnits="userSpaceOnUse">
                    <stop stop-color="#975EFF"/>
                    <stop offset="1" stop-color="#5DC8FA"/>
                    </linearGradient>
                    <linearGradient id="paint4_linear_2781_2880" x1="66.293" y1="11.1417" x2="68.4749" y2="42.9769" gradientUnits="userSpaceOnUse">
                    <stop stop-color="#975EFF"/>
                    <stop offset="1" stop-color="#5DC8FA"/>
                    </linearGradient>
                    <linearGradient id="paint5_linear_2781_2880" x1="66.2929" y1="11.1417" x2="68.4748" y2="42.9768" gradientUnits="userSpaceOnUse">
                    <stop stop-color="#975EFF"/>
                    <stop offset="1" stop-color="#5DC8FA"/>
                    </linearGradient>
                    <linearGradient id="paint6_linear_2781_2880" x1="66.2932" y1="11.1417" x2="68.4751" y2="42.9769" gradientUnits="userSpaceOnUse">
                    <stop stop-color="#975EFF"/>
                    <stop offset="1" stop-color="#5DC8FA"/>
                    </linearGradient>
                    <linearGradient id="paint7_linear_2781_2880" x1="66.2932" y1="11.1417" x2="68.4752" y2="42.9768" gradientUnits="userSpaceOnUse">
                    <stop stop-color="#975EFF"/>
                    <stop offset="1" stop-color="#5DC8FA"/>
                    </linearGradient>
                    <linearGradient id="paint8_linear_2781_2880" x1="66.2929" y1="11.1417" x2="68.4749" y2="42.9768" gradientUnits="userSpaceOnUse">
                    <stop stop-color="#975EFF"/>
                    <stop offset="1" stop-color="#5DC8FA"/>
                    </linearGradient>
                    <linearGradient id="paint9_linear_2781_2880" x1="66.2932" y1="11.1417" x2="68.4751" y2="42.9769" gradientUnits="userSpaceOnUse">
                    <stop stop-color="#975EFF"/>
                    <stop offset="1" stop-color="#5DC8FA"/>
                    </linearGradient>
                    <linearGradient id="paint10_linear_2781_2880" x1="68.6216" y1="8.12414" x2="69.4172" y2="25.4534" gradientUnits="userSpaceOnUse">
                    <stop stop-color="#975EFF"/>
                    <stop offset="1" stop-color="#5DC8FA"/>
                    </linearGradient>
                </defs>
                </svg>
            </div>
          </div>
          <div className="ai-chat-actions">
            <div className="ai-chat-menu-container">
              <button 
                className="ai-chat-menu-btn"
                onClick={() => setShowChatHistoryButton(!showChatHistoryButton)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="4" height="16" viewBox="0 0 4 16" fill="none">
                    <circle cx="1.6" cy="1.6" r="1.6" fill="#282828"/>
                    <circle cx="1.6" cy="8" r="1.6" fill="#282828"/>
                    <circle cx="1.6" cy="14.4" r="1.6" fill="#282828"/>
                </svg>
              </button>
              {showChatHistoryButton && (
                <div className="ai-chat-menu-dropdown">
                  <button 
                    className="ai-chat-history-btn"
                    onClick={handleChatHistoryClick}
                  >
                    Chat history
                  </button>
                </div>
              )}
            </div>
            <button className="ai-chat-close-btn" onClick={onClose}>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path d="M16.5 1L8.75 8.75L1 16.5" stroke="#282828" stroke-width="1.5"/>
                    <path d="M16.5 16.5L8.75 8.75L1 0.999999" stroke="#282828" stroke-width="1.5"/>
                </svg>
            </button>
          </div>
        </div>

        {/* Back button - only show when not on welcome page */}
        {currentView !== 'welcome' && (
          <div className="ai-chat-back">
            <button className="ai-back-btn" onClick={handleBackClick}>
            <svg xmlns="http://www.w3.org/2000/svg" width="8" height="13" viewBox="0 0 8 13" fill="none">
                <path d="M7 1L2 6.5L7 12" stroke="#405BFF" stroke-width="1.8"/>
            </svg>
              Back
            </button>
          </div>
        )}

        {/* Chat History Main View */}
        {currentView === 'history' && (
          <div className="ai-chat-history-main">
            <div className="ai-chat-history-header">
              <h3>Chats</h3>
            </div>
            <div className="ai-chat-history-list">
              {isLoadingHistory ? (
                <div className="ai-loading">Loading chat history...</div>
              ) : chatHistory.length === 0 ? (
                <div className="ai-no-chats">No chat history found</div>
              ) : (
                chatHistory.map((chat) => (
                  <div
                    key={chat._id}
                    className={`ai-chat-history-item ${currentChatId === chat._id ? 'active' : ''} ${loadingChatId === chat._id ? 'loading' : ''}`}
                    onClick={() => handleChatSelect(chat._id)}
                  >
                    <span className="chat-name">{chat.title}</span>
                    <div className="chat-meta">
                      <span className="chat-date">{formatChatDate(chat.date)}</span>
                      <span className="chat-message-count">{chat.totalMessages} messages</span>
                      {loadingChatId === chat._id ? (
                     
                        <div className="chat-loading-spinner">
                          <div className="ai-spinner"></div>
                        </div>
                      ) : (
                        <button 
                          className="delete-chat-btn"
                          onClick={(e) => handleDeleteChat(chat._id, e)}
                        >
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="15" viewBox="0 0 14 15" fill="none">
                          <path d="M11.7157 13.5198L12.0371 3.26809H13.4747V2.91519H9.73692V1.98001C9.73692 1.4683 9.34792 1.06247 8.85745 1.06247H5.6778C5.18732 1.06247 4.79832 1.4683 4.79832 1.98001V2.91519H1.06055V3.26809H2.49815L2.8195 13.5198C2.83641 13.9962 3.2085 14.3667 3.66515 14.3667H10.8532C11.3267 14.3667 11.6988 13.9962 11.7157 13.5198ZM5.13658 1.98001C5.13658 1.6624 5.37337 1.41537 5.6778 1.41537H8.85745C9.16188 1.41537 9.39866 1.6624 9.39866 1.98001V2.91519H5.13658V1.98001ZM3.15776 13.5021L2.83641 3.26809H11.6988L11.3775 13.5021C11.3606 13.7845 11.1407 14.0139 10.8701 14.0139H3.66515C3.39454 14.0139 3.17467 13.7845 3.15776 13.5021Z" fill="#405BFF" stroke="#282828" stroke-width="0.5"/>
                          <path d="M10.8533 14.43H3.66501C3.17483 14.43 2.77707 14.0314 2.75895 13.5225L2.43942 3.33165H1V2.85272H4.73778V1.98055C4.73778 1.43073 5.15064 1 5.67766 1H8.8573C9.38433 1 9.79718 1.43073 9.79718 1.98055V2.85272H13.535V3.33165H12.0952L11.7778 13.4573H11.7781L11.7757 13.5225C11.7576 14.0314 11.3523 14.43 10.853 14.43H10.8533ZM1.12081 3.2053H2.5566L2.87976 13.5178C2.89547 13.9583 3.24037 14.3036 3.66501 14.3036H10.8533C11.2919 14.3036 11.635 13.97 11.6549 13.526V13.5178L11.9784 3.2053H13.4142V2.97844H9.67638V1.98024C9.67638 1.50099 9.31667 1.12572 8.8573 1.12572H5.67766C5.21829 1.12572 4.85858 1.50099 4.85858 1.98024V2.97844H1.12081V3.2053ZM3.66501 14.0771C3.36601 14.0771 3.11654 13.8263 3.09752 13.5062V13.5043L2.77405 3.20499H11.7612L11.4377 13.5043C11.4184 13.8263 11.1693 14.0768 10.87 14.0768H3.66501V14.0771ZM3.21802 13.4992C3.23373 13.7526 3.42974 13.9511 3.66501 13.9511H10.87C11.1055 13.9511 11.3018 13.7522 11.3169 13.4983L11.6362 3.33102H2.89849L3.21772 13.4989L3.21802 13.4992ZM9.45892 2.97844H5.07604V1.98024C5.07604 1.62829 5.3403 1.35258 5.67766 1.35258H8.8573C9.19466 1.35258 9.45892 1.62829 9.45892 1.98024V2.97844ZM5.19684 2.8524H9.33812V1.98024C9.33812 1.69887 9.12701 1.47862 8.8573 1.47862H5.67766C5.40795 1.47862 5.19684 1.69887 5.19684 1.98024V2.8524Z" fill="#405BFF" stroke="#282828" stroke-width="0.5"/>
                          <path d="M7.09766 5.95013H7.43592V11.226H7.09766V5.95013Z" fill="#405BFF" stroke="#282828" stroke-width="0.5"/>
                          <path d="M7.49618 11.289H7.03711V5.88708H7.49618V11.289ZM7.15792 11.1629H7.37537V6.01312H7.15792V11.1629Z" fill="#405BFF" stroke="#282828" stroke-width="0.5"/>
                          <path d="M5.06836 5.95013H5.40662V11.226H5.06836V5.95013Z" fill="#405BFF" stroke="#282828" stroke-width="0.5"/>
                          <path d="M5.46688 11.289H5.00781V5.88708H5.46688V11.289ZM5.12862 11.1629H5.34607V6.01312H5.12862V11.1629Z" fill="#405BFF" stroke="#282828" stroke-width="0.5"/>
                          <path d="M9.12695 5.95013H9.46521V11.226H9.12695V5.95013Z" fill="#405BFF" stroke="#282828" stroke-width="0.5"/>
                          <path d="M9.52645 11.289H9.06738V5.88708H9.52645V11.289ZM9.18819 11.1629H9.40564V6.01312H9.18819V11.1629Z" fill="#405BFF" stroke="#282828" stroke-width="0.5"/>
                        </svg>
                        </button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Messages - only show when not in history view */}
        {currentView !== 'history' && (
          <div className="ai-chat-messages" ref={messagesContainerRef}>
            {currentView === 'welcome' ? (
              <div className="ai-home-welcome">
                <p className="ai-home-title">Hi there..! I’m your Talenton assistant.</p>
                <p className="ai-home-subtitle">I’m here to help you get the most out of Talenton - what can I assist with?</p>
              </div>
            ) : (
              messages.map((message) => (
                <div key={message.id} className={`ai-message ${message.type}`}>
                  <div className="ai-message-content">
                    {message.type === 'ai' && message.processingMs != null && (
                      <div className="ai-processing-time" title={`Processing time: ${message.processingMs} ms`}>
                        <span>Thought for {Math.round(message.processingMs / 1000)}s</span>
                        <button
                          type="button"
                          className="ai-copy-btn"
                          aria-label="Copy response"
                          onClick={async (e) => {
                            try {
                              await navigator.clipboard.writeText(message.content);
                              setCopiedMessageId(message.id);
                              setTimeout(() => setCopiedMessageId(null), 1500);
                            } catch (err) {
                              console.error('Copy failed', err);
                            }
                          }}
                        >
                          {copiedMessageId === message.id ? (
                            // check icon
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                              <path d="M13.3333 4.6665L6.5 11.4998L3 7.99984" stroke="#6A7988" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          ) : (
                            // copy icon
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                              <rect x="5.333" y="5.333" width="8.667" height="8.667" rx="1.333" stroke="#6A7988" strokeWidth="1.2"/>
                              <rect x="2" y="2" width="8" height="8" rx="1.333" stroke="#6A7988" strokeWidth="1.2"/>
                            </svg>
                          )}
                        </button>
                      </div>
                    )}
                    {message.content.split('\n').map((line, index) => (
                      <div key={index}>
                        {parseHighlightedText(line)}
                      </div>
                    ))}
                  </div>
                  {message.suggestions && message.suggestions.length > 0 && (
                    <div className="ai-suggestions-box">
                      <div className="ai-suggestions-header">Related questions you might ask:</div>
                      <div className="ai-suggestions-list">
                        {message.suggestions.map((suggestion, index) => (
                          <div 
                            key={suggestion.id || index} 
                            className="ai-suggestion-item"
                            onClick={() => {
                              setInputValue(suggestion.question);
                            }}
                          >
                            {suggestion.question}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
            
            {isTyping && (
              <div className="ai-message ai">
                <div className="ai-typing-indicator">
                  <div className="ai-logo-icon small">
                  <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 21 21" fill="none">
                    <path d="M10.5 0C16.299 0 21 4.70101 21 10.5C21 16.299 16.299 21 10.5 21C4.70101 21 0 16.299 0 10.5C0 4.70101 4.70101 0 10.5 0ZM10.5 0.84C5.16493 0.84 0.84 5.16493 0.84 10.5C0.84 15.8351 5.16493 20.16 10.5 20.16C15.8351 20.16 20.16 15.8351 20.16 10.5C20.16 5.16493 15.8351 0.84 10.5 0.84ZM11.8973 8.59469V11.8655L11.8967 11.8787L11.8852 11.8716L9.27008 10.2659V13.4263L11.8852 11.8716L11.8967 11.8787L11.8973 11.8655L14.595 10.2621V13.4411L9.27008 16.7109V13.4312L6.09 15.383V12.151L9.26625 10.2648L6.09 8.31414V5.145L11.8973 8.59469ZM13.9508 4.62492C13.9824 5.21243 14.2805 6.34123 15.7391 6.79219L15.7221 7.06398C15.4095 7.11948 14.9625 7.32092 14.595 7.68305C14.2534 8.01968 13.9832 8.49207 13.9508 9.11367L13.9475 9.24H13.6675C13.6675 8.82837 13.4499 8.31401 13.0895 7.88211C12.7299 7.45122 12.2477 7.12598 11.743 7.06508L11.76 6.78617C12.0296 6.78617 12.5092 6.60459 12.9248 6.21797C13.3344 5.83696 13.6675 5.26944 13.6675 4.515H13.9475L13.9508 4.62492Z" fill="url(#paint0_linear_2781_3874)"/>
                    <defs>
                    <linearGradient id="paint0_linear_2781_3874" x1="5.83333" y1="4.375" x2="19.8333" y2="21" gradientUnits="userSpaceOnUse">
                        <stop stop-color="#975EFF"/>
                        <stop offset="1" stop-color="#5DC8FA"/>
                    </linearGradient>
                    </defs>
                    </svg>
                  </div>
                  <span>Looking for your answer...</span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
            {showScrollToBottom && (
              <>
                <div className="ai-bottom-fade" />
                <div className="ai-scroll-bottom">
                  <button className="ai-scroll-btn" onClick={scrollToBottom} aria-label="Scroll to bottom">
                    <svg xmlns="http://www.w3.org/2000/svg" width="37" height="37" viewBox="0 0 37 37" fill="none">
                        <circle cx="18.5" cy="18.5" r="17.5" fill="white" stroke="#6A7988"/>
                        <path d="M11 16L19 23L27 16" stroke="#6A7988"/>
                    </svg>
                  </button>
                </div>
              </>
            )}
          </div>
        )}

         {/* Input - always visible */}
         <div className="ai-chat-input">
          <input
            type="text"
            placeholder="Ask anything."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyPress}
            className="ai-input-field"
          />
          <button 
            className="ai-send-btn"
            onClick={handleSendMessage}
            disabled={!inputValue.trim()}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="26" height="22" viewBox="0 0 26 22" fill="none">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M1.01485 5.16404C0.596858 4.73175 0.463069 4.09856 0.669914 3.53368C0.87702 2.96925 1.38929 2.5727 1.9876 2.51337L23.6833 0.363834C24.2958 0.302947 24.8878 0.606979 25.1957 1.14026C25.5036 1.67354 25.4709 2.33825 25.1119 2.83826L12.4025 20.5525C12.052 21.041 11.4524 21.2864 10.86 21.1835C10.2674 21.0802 9.78596 20.6477 9.62058 20.0696L7.80401 13.7189C7.64337 13.1573 7.80784 12.5536 8.23145 12.1508L11.4925 9.0518L7.17814 10.3265C6.61758 10.4919 6.01246 10.3325 5.60646 9.91261L1.01485 5.16404Z" fill="#405BFF"/>
            </svg>
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="ai-delete-confirm-overlay" ref={deleteModalRef}>
          <div className="ai-delete-confirm-modal">
            <div className="ai-delete-confirm-header">
              <h3>Delete Chat</h3>
            </div>
            <div className="ai-delete-confirm-body">
              <p>Are you sure you want to delete this chat? This action cannot be undone.</p>
            </div>
            <div className="ai-delete-confirm-actions">
              <button 
                className="ai-delete-cancel-btn" 
                onClick={cancelDeleteChat}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button 
                className="ai-delete-confirm-btn" 
                onClick={confirmDeleteChat}
                disabled={isDeleting}
              >
                {isDeleting ? <div className="ai-btn-spinner" aria-label="Deleting" /> : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIChat;
